// 編集中の文字選択ドラッグが編集面（surface）の外へ出たときに、最後に枠内で成立した選択を保つフック。
//
// 背景（Issue #160）:
// - Chromium では、編集中の contentEditable で始めたドラッグ選択のポインタが編集面の外へ出ると、
//   選択の終点が編集要素の先頭へ寄る（左・右・上のどこへ出ても、起点より前の本文がまとめて選ばれる）。
//   利用者には「枠外へ出た瞬間に先行行まで選ばれた」ように見える（実ブラウザーで再現済み）。
// - この更新は、mousemove の既定処理を止めても止まらない（Chromium で確かめた）。そのため、ブラウザーが
//   広げた選択を、描画の前と、エディターが選択を読む前に、保存した選択へ戻す。
//
// 振る舞い:
// - 編集面の中で始めた主ボタンのマウス・ペンのドラッグだけを追跡する。タッチの長押し選択（ネイティブの
//   つまみ）、主ボタン以外、編集面の外で始めたポインタには関与しない。クリック・ダブルクリック・
//   Shift+クリック・枠内のドラッグはブラウザー標準の選択のまま使う（このフックは既定処理を止めない）。
// - ポインタが編集面の上にあり、選択の anchor / focus がどちらも編集面の中にあるときだけ、その選択を
//   anchor / focus の組（向きを含む）として保存する。
// - 編集面の外にいる間は、選択が保存値から外れたら保存値へ戻す。外へ動くたびに requestAnimationFrame で
//   描画の前に戻し（広がった選択を画面に出さない）、呼び出し側も選択を読む前に reconcileSelection を呼ぶ
//   （共有キャレット・書式の状態へ誤った範囲を流さない）。
// - 編集面へ戻ったら戻すのをやめ、ブラウザー標準のドラッグ選択へ任せる（起点は保存値の anchor のまま）。
// - pointerup / pointercancel / 主ボタンが離れた pointermove / 選択を動かすキー入力 / window の blur /
//   編集の終了 / アンマウントで追跡を終える。枠外で終えるとき（編集の終了・アンマウントを除く）は、終える
//   前に保存値へ戻す。最後の有効な選択はそのまま残し、ツールバーなどの通常の操作へ引き継ぐ。
// - 保存した位置が DOM の置き換え（IME の確定・他参加者の更新）で使えなくなったら、古い位置は戻さない。
//   枠内にいるなら、そのとき編集面の中で成立している選択を新しい保存値にする（利用者に見えている最新の
//   選択）。枠外で気づいたときは、起点（anchor）のキャレットを新しい保存値にして戻し、そのドラッグの残りの
//   枠外でも同じキャレットを保つ（本文の先頭からの選択を残さない）。
//
// 内外の判定は document.elementFromPoint のヒット判定（ポインタ位置の最前面の要素が編集面の子孫か）で行う。
// ステージの拡大率・図形の回転・付箋の overflow クリップ・上に重なった別の図形を、ブラウザー自身の描画どおりに
// 扱えるため、回転後の外接矩形の空白を編集面の中と誤認しない。
//
// 採用しないもの:
// - ポインタ全般の preventDefault や、編集面での常時 pointer capture。ネイティブの選択（クリック・
//   ダブルクリック・Shift+クリック・枠内のドラッグ）を壊すため。
// - mousemove の preventDefault。Chromium のドラッグ選択の更新は止まらず、効果が無い。
// - 選択を本文全体の先頭・末尾へ丸めること。保存した anchor / focus をそのまま戻し、逆方向の選択も保つ。

import { useCallback, useEffect, useRef, type RefObject } from "react";

/** 選択の片端（DOM 上の位置）。 */
interface SelectionPoint {
  readonly node: Node;
  readonly offset: number;
}

/** 保存する選択。anchor / focus の組なので、逆方向（右から左）の選択の向きも保つ。 */
interface SelectionSnapshot {
  readonly anchor: SelectionPoint;
  readonly focus: SelectionPoint;
}

/** 追跡中のドラッグの状態。 */
interface DragGesture {
  readonly pointerId: number;
  /** pointerdown の位置（クライアント座標）。保存できる選択が無いまま枠外へ出たときの代替に使う。 */
  readonly downX: number;
  readonly downY: number;
  /** 直前のポインタ位置（pointerdown または pointermove）が編集面の上だったか。 */
  inside: boolean;
  /** 最後に枠内で成立した選択。まだ無い・使えなくなったときは null（枠外では起点のキャレットへ戻す）。 */
  snapshot: SelectionSnapshot | null;
}

interface Options {
  /** 編集面（contentEditable の要素）。 */
  readonly elementRef: RefObject<HTMLElement | null>;
  /** 編集中（かつ編集可能）のときだけ true。false の間は何も追跡しない。 */
  readonly enabled: boolean;
  /** IME の変換中か。変換中は選択を戻さない（変換を壊さないため）。 */
  readonly isComposing: () => boolean;
}

export interface BoundedTextSelection {
  /**
   * 追跡中のドラッグが編集面の外にあり、DOM の選択が保存値から外れていれば保存値へ戻す
   * （保存値が無い・使えなくなったときは、起点〈anchor〉のキャレットへ戻す）。
   * 呼び出し側は、選択を読んで共有・書式へ流す前（selectionchange の処理の先頭など）に呼ぶ。
   * 追跡していないとき・枠内にいるとき・IME の変換中は何もしない。
   */
  readonly reconcileSelection: () => void;
}

/** 選択を動かさない修飾キー。ドラッグ中に押されても追跡を続ける。 */
const MODIFIER_KEYS: ReadonlySet<string> = new Set(["Shift", "Control", "Alt", "Meta", "CapsLock"]);

/** PointerEvent.buttons の主ボタン（左）のビット。 */
const PRIMARY_BUTTON_BIT = 1;

export function useBoundedTextSelection(options: Options): BoundedTextSelection {
  const { elementRef, enabled } = options;
  const gestureRef = useRef<DragGesture | null>(null);
  // 自分で選択を戻している最中か。selectionchange を同期的に出す実装でも再入しないための目印
  // （通常の実装は非同期に出すため、戻した後の selectionchange は同一範囲の比較で止まる）。
  const restoringRef = useRef(false);
  // IME 判定は毎レンダーで最新の関数へ差し替え、effect の依存へは載せない（リスナを張り直さない）。
  const isComposingRef = useRef(options.isComposing);
  isComposingRef.current = options.isComposing;

  const reconcileSelection = useCallback((): void => {
    const gesture = gestureRef.current;
    const el = elementRef.current;
    if (!gesture || gesture.inside || !el || restoringRef.current) return;
    if (isComposingRef.current()) return;
    restoringRef.current = true;
    try {
      restoreSnapshot(el, gesture);
    } finally {
      restoringRef.current = false;
    }
  }, [elementRef]);

  useEffect(() => {
    if (!enabled) {
      gestureRef.current = null;
      return undefined;
    }
    const el = elementRef.current;
    if (!el) return undefined;
    const doc = el.ownerDocument;
    const win = doc.defaultView;
    // 描画の前に保存値へ戻す予約（requestAnimationFrame の ID）。予約は 1 つだけ持つ。
    let frame: number | null = null;

    const cancelRestoreBeforePaint = (): void => {
      if (frame !== null) win?.cancelAnimationFrame(frame);
      frame = null;
    };

    // 枠外へ動いた後、ブラウザーがその移動で広げた選択を、描画の前に保存値へ戻す。ブラウザーの更新は
    // pointermove の処理の後に走り、selectionchange は描画の後に届くことがあるため、それを待つと
    // 広がった選択が一瞬だけ画面に出る。
    const scheduleRestoreBeforePaint = (): void => {
      if (frame !== null || !win) return;
      frame = win.requestAnimationFrame(() => {
        frame = null;
        reconcileSelection();
      });
    };

    const endGesture = (): void => {
      cancelRestoreBeforePaint();
      gestureRef.current = null;
    };

    // 追跡を終える。枠外で終えるときは、終える前に広がった選択を保存値へ戻す。pointerup だけでなく、
    // pointercancel・pointerup の取りこぼし・キー入力・window の blur で終える場合も同じ扱いにする
    // （戻さずに終えると、以後の selectionchange では戻せず、広がった選択がそのまま残るため）。
    const finishGesture = (): void => {
      const gesture = gestureRef.current;
      if (gesture && !gesture.inside) reconcileSelection();
      endGesture();
    };

    const onPointerDown = (event: PointerEvent): void => {
      // タッチはネイティブの長押し選択・つまみに任せる。主ボタン以外・副ポインタも追跡しない。
      if (event.pointerType === "touch" || !event.isPrimary || event.button !== 0) return;
      cancelRestoreBeforePaint();
      gestureRef.current = {
        pointerId: event.pointerId,
        downX: event.clientX,
        downY: event.clientY,
        inside: true,
        snapshot: null,
      };
    };

    const onPointerMove = (event: PointerEvent): void => {
      const gesture = gestureRef.current;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      // 主ボタンが離れているのに pointerup を取りこぼした（ウィンドウ外で離した等）なら追跡を終える。
      if ((event.buttons & PRIMARY_BUTTON_BIT) === 0) {
        finishGesture();
        return;
      }
      // この時点の DOM の選択は、直前の位置までのドラッグを反映している（この移動でのブラウザーの
      // 更新はこの後に走る）。直前の位置が枠内だったときだけ、保存の候補にする。
      if (gesture.inside) {
        const current = readSelectionWithin(el);
        if (current) {
          // DOM が置き換わった後でも、枠内で成立している今の選択が、利用者に見えている最新の選択になる。
          gesture.snapshot = current;
        } else if (gesture.snapshot !== null && !isRestorable(el, gesture.snapshot)) {
          // 今の選択は編集面に収まらず、保存値も DOM の置き換えで使えない。枠外では起点のキャレットへ戻す。
          gesture.snapshot = null;
        }
      }
      const inside = isPointInsideSurface(el, event.clientX, event.clientY);
      if (!inside && gesture.inside && gesture.snapshot === null) {
        // 保存できる選択が無いまま一気に枠外へ出た。押した位置のキャレットを代わりに使い、
        // 本文の先頭からの全選択にはしない。
        gesture.snapshot = caretSnapshotAtPoint(el, gesture.downX, gesture.downY);
      }
      gesture.inside = inside;
      if (inside) cancelRestoreBeforePaint();
      else scheduleRestoreBeforePaint();
    };

    const onPointerUp = (event: PointerEvent): void => {
      const gesture = gestureRef.current;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      // 枠外で離したときは、離すまでに選択が保存値から外れていないかを最後に確かめてから終える。
      finishGesture();
    };

    const onPointerCancel = (event: PointerEvent): void => {
      const gesture = gestureRef.current;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      finishGesture();
    };

    // キーボードでの選択（Shift+矢印・Ctrl+A・Home/End など）はこの保護の対象外。
    // 選択を動かし得るキーが押されたら追跡を終え、以後の選択変更を戻さない（キーの既定処理は
    // この後に走るため、枠外で広がっていた選択は先に保存値へ戻してから、キーの操作に任せる）。
    // キーボードからの IME の入力も、compositionstart より先に届く keydown（key=Process）で終える
    // ため、変換中に追跡が残って、変換で動いた選択を後から上書きすることはない。
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!gestureRef.current || MODIFIER_KEYS.has(event.key)) return;
      finishGesture();
    };

    el.addEventListener("pointerdown", onPointerDown);
    doc.addEventListener("pointermove", onPointerMove, { capture: true });
    doc.addEventListener("pointerup", onPointerUp, { capture: true });
    doc.addEventListener("pointercancel", onPointerCancel, { capture: true });
    doc.addEventListener("keydown", onKeyDown, { capture: true });
    win?.addEventListener("blur", finishGesture);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      doc.removeEventListener("pointermove", onPointerMove, { capture: true });
      doc.removeEventListener("pointerup", onPointerUp, { capture: true });
      doc.removeEventListener("pointercancel", onPointerCancel, { capture: true });
      doc.removeEventListener("keydown", onKeyDown, { capture: true });
      win?.removeEventListener("blur", finishGesture);
      // 編集の終了・アンマウントでは編集要素ごと作り直されるため、選択は戻さずに追跡だけを終える。
      endGesture();
    };
  }, [enabled, elementRef, reconcileSelection]);

  return { reconcileSelection };
}

/**
 * ポインタ位置が編集面の上か。最前面の要素が編集面（またはその子孫）なら内側とみなす。
 * elementFromPoint を持たない環境（テスト用の DOM 実装など）では外側として扱う。
 */
export function isPointInsideSurface(el: HTMLElement, clientX: number, clientY: number): boolean {
  const doc: { elementFromPoint?: ((x: number, y: number) => Element | null) | undefined } =
    el.ownerDocument;
  const hit = doc.elementFromPoint?.(clientX, clientY) ?? null;
  return hit !== null && el.contains(hit);
}

/** 現在の選択の anchor / focus がどちらも編集面の中にあれば、その組を返す。 */
function readSelectionWithin(el: HTMLElement): SelectionSnapshot | null {
  const selection = el.ownerDocument.defaultView?.getSelection() ?? null;
  if (!selection || selection.rangeCount === 0) return null;
  const { anchorNode, focusNode } = selection;
  if (!anchorNode || !focusNode || !el.contains(anchorNode) || !el.contains(focusNode)) return null;
  return {
    anchor: { node: anchorNode, offset: selection.anchorOffset },
    focus: { node: focusNode, offset: selection.focusOffset },
  };
}

/** 指定位置のキャレット（折り畳んだ選択）を返す。編集面の外や取得できない場合は null。 */
function caretSnapshotAtPoint(
  el: HTMLElement,
  clientX: number,
  clientY: number
): SelectionSnapshot | null {
  // どちらの API も実装差がある（caretRangeFromPoint は WebKit / Chromium、caretPositionFromPoint は
  // Firefox と新しい Chromium）ため、持っている方を使う。
  const doc: {
    caretRangeFromPoint?: ((x: number, y: number) => Range | null) | undefined;
    caretPositionFromPoint?:
      | ((x: number, y: number) => { readonly offsetNode: Node; readonly offset: number } | null)
      | undefined;
  } = el.ownerDocument;
  const position = doc.caretPositionFromPoint?.(clientX, clientY) ?? null;
  const range = position ? null : (doc.caretRangeFromPoint?.(clientX, clientY) ?? null);
  const node = position?.offsetNode ?? range?.startContainer ?? null;
  const offset = position?.offset ?? range?.startOffset ?? 0;
  if (!node || !el.contains(node)) return null;
  const point = { node, offset };
  return { anchor: point, focus: point };
}

/**
 * 選択を保存値へ戻す。保存値が無い、または DOM の置き換え（IME の確定・他参加者の更新・本文の短縮）で
 * 使えなくなっていたときは、古い位置は戻さず、起点（anchor）のキャレットを新しい保存値にして戻す。
 */
function restoreSnapshot(el: HTMLElement, gesture: DragGesture): void {
  const selection = el.ownerDocument.defaultView?.getSelection() ?? null;
  if (!selection) return;
  if (gesture.snapshot === null || !isRestorable(el, gesture.snapshot)) {
    gesture.snapshot = anchorCaretWithin(el, selection);
  }
  const snapshot = gesture.snapshot;
  if (snapshot === null || isSameSelection(selection, snapshot)) return;
  try {
    // anchor / focus の組で戻し、逆方向の選択の向きを保つ（Range の start / end では向きが失われる）。
    selection.setBaseAndExtent(
      snapshot.anchor.node,
      snapshot.anchor.offset,
      snapshot.focus.node,
      snapshot.focus.offset
    );
  } catch {
    // 戻せない位置だった（実装差）。以後は起点のキャレットを保つ。
    const caret = anchorCaretWithin(el, selection);
    gesture.snapshot = caret;
    if (caret) selection.collapse(caret.anchor.node, caret.anchor.offset);
  }
}

/** 保存した両端が、今も編集面の中の有効な位置か。 */
function isRestorable(el: HTMLElement, snapshot: SelectionSnapshot): boolean {
  return [snapshot.anchor, snapshot.focus].every(
    ({ node, offset }) => node.isConnected && el.contains(node) && offset <= nodeLength(node)
  );
}

/** DOM 位置として取り得るオフセットの上限（文字データは文字数、要素は子の数）。 */
function nodeLength(node: Node): number {
  return node instanceof CharacterData ? node.length : node.childNodes.length;
}

function isSameSelection(selection: Selection, snapshot: SelectionSnapshot): boolean {
  return (
    selection.anchorNode === snapshot.anchor.node &&
    selection.anchorOffset === snapshot.anchor.offset &&
    selection.focusNode === snapshot.focus.node &&
    selection.focusOffset === snapshot.focus.offset
  );
}

/** 選択の起点（anchor）が編集面の中なら、そこへ畳んだキャレットを返す。 */
function anchorCaretWithin(el: HTMLElement, selection: Selection): SelectionSnapshot | null {
  const { anchorNode, anchorOffset } = selection;
  if (!anchorNode || !el.contains(anchorNode)) return null;
  const point = { node: anchorNode, offset: anchorOffset };
  return { anchor: point, focus: point };
}
